import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CreditCard, CalendarDays, CalendarRange, CalendarClock, AlertTriangle } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinancialRadar({ signature }) {
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/finance/radar").then((x) => setR(x.data)).catch(() => {});
  }, [signature]);

  if (!r) return null;

  const targets = [
    { k: "daily", label: "Daily Goal", value: r.daily_goal_needed, icon: CalendarClock },
    { k: "weekly", label: "Weekly Goal", value: r.weekly_goal_needed, icon: CalendarDays },
    { k: "monthly", label: "Monthly Goal", value: r.monthly_goal_needed, icon: CalendarRange },
  ];

  return (
    <section className="border border-[var(--line)] bg-white h-full" data-testid="financial-radar">
      <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
        <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[var(--accent)]" /> Bills & Financial Radar
        </h2>
        <span className="label-mono text-[var(--text-2)]">{r.count} in 30 days</span>
      </div>

      <div className="p-6">
        <div className="bg-[#1A1A18] text-[#F9F9F7] p-6" data-testid="radar-total">
          <p className="label-mono text-[#A3A39F]">Total upcoming bills · next 30 days</p>
          <p className="font-serif text-5xl sm:text-6xl tracking-tight mt-2 text-[#E37B5B]">{money(r.total_upcoming_bills)}</p>
        </div>

        {r.overdue_count > 0 && (
          <div className="mt-4 border-2 border-[var(--danger)] bg-[var(--danger)]/5 p-4" data-testid="radar-overdue">
            <div className="flex items-center justify-between">
              <p className="label-mono text-[var(--danger)] flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Overdue bills · past due ({r.overdue_count})</p>
              <p className="font-mono text-lg font-bold text-[var(--danger)]" data-testid="radar-overdue-total">{money(r.overdue_total)}</p>
            </div>
            <ul className="mt-2 space-y-1" data-testid="radar-overdue-list">
              {r.overdue_items.map((o, i) => (
                <li key={i} data-testid="radar-overdue-item" className="flex items-center justify-between text-xs">
                  <span className="truncate pr-2">{o.title} <span className="text-[var(--text-2)]">· due {new Date(o.due).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span></span>
                  <span className="font-mono text-[var(--danger)] shrink-0">{money(o.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mt-4">
          {targets.map(({ k, label, value, icon: Icon }) => (
            <div key={k} data-testid={`radar-${k}`} className="border border-[var(--line)] p-4">
              <Icon className="w-4 h-4 text-[var(--accent)]" />
              <p className="label-mono text-[var(--text-2)] mt-2">{label}</p>
              <p className="font-mono text-lg sm:text-xl mt-1 tracking-tight">{money(value)}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[var(--text-2)] mt-3">
          Save the daily goal and you'll cover every bill due this month. Mark a bill paid to watch targets drop live.
        </p>

        {r.weekly_breakdown && r.weekly_breakdown.some((w) => w.total > 0) && (
          <div className="mt-5" data-testid="radar-trends">
            <p className="label-mono text-[var(--text-2)] mb-2">Upcoming by week</p>
            <div className="flex items-end gap-2 h-28">
              {r.weekly_breakdown.map((w, i) => {
                const max = Math.max(...r.weekly_breakdown.map((x) => x.total), 1);
                const h = Math.round((w.total / max) * 100);
                return (
                  <div key={i} data-testid={`radar-week-${i}`} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] font-mono text-[var(--text-2)] h-3">{w.total ? money(w.total) : ""}</span>
                    <div className="w-full bg-[var(--accent)]/10 flex items-end flex-1">
                      <div className="w-full bg-[var(--accent)] transition-all duration-500" style={{ height: `${h}%` }} title={money(w.total)} />
                    </div>
                    <span className="text-[10px] font-mono text-[var(--text-2)]">{w.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
